// @flow

import { values } from 'lodash';
import { updateTimerPreferences, getListOfSortedCarouselIds } from './utils';
import { DEFAULT_RECORD_SETTINGS } from '../dvr/types';

import type { Action, State, CarouselPreference } from './types';

const initialState: State = {
  accountPreferences: {
    fetching: false,
    error: false,
    preferences: undefined,
  },
  profilePreferences: {
    fetching: false,
    error: false,
    preferences: undefined,
  },
  dynaDevices: {
    fetching: false,
    error: false,
    devices: [],
  },
  legacyDevices: {
    fetching: false,
    error: false,
    devices: [],
  },
  removeDevice: {
    currentlyRemovingDevices: [],
    errorDescription: '',
  },
  gameFinderPreferences: {
    showScores: undefined,
    fetching: false,
    errorFetching: false,
    errorSaving: true,
  },
  guidePreferences: undefined,
  timerPreferences: {
    fetching: false,
    errorFetching: false,
    errorSaving: false,
    preferences: undefined,
  },
};

const reducer = (state: State = initialState, action: Action): State => {
  switch (action.type) {
    case 'REQUEST_PROFILE_PREFERENCES': {
      return {
        ...state,
        profilePreferences: {
          fetching: true,
          error: false,
        },
      };
    }
    case 'RECEIVE_PROFILE_PREFERENCES': {
      const { payload } = action;
      const { carousels, ...restOfPayload } = payload;
      const carouselsAsArray: Array<CarouselPreference> = values(carousels);

      const {
        guide_channels_sort_ascending: guideChannelsSortAscending,
        guide_channel_sources: guideChannelSources,
      } = restOfPayload;

      return {
        ...state,
        profilePreferences: {
          fetching: false,
          error: false,
          preferences: {
            carousels: carouselsAsArray,
            /* It is required for the list of carousels (CarouselSelector component)
                to be displayed in the same order all the time.
                The only way to do it (without involving backend) is to use alphabetically sorted
                list of carousels. */
            sorterCarouselIds: getListOfSortedCarouselIds(carouselsAsArray),
            guideChannelsSorting: {
              ascending: guideChannelsSortAscending,
              fetching: false,
              error: false,
            },
            guideChannelSources,
          },
        },
      };
    }
    case 'REQUEST_PROFILE_PREFERENCES_ERROR': {
      return {
        ...state,
        profilePreferences: {
          fetching: false,
          error: true,
        },
      };
    }
    case 'REQUEST_ACCOUNT_PREFERENCES': {
      return {
        ...state,
        accountPreferences: {
          fetching: true,
          error: false,
        },
      };
    }
    case 'RECEIVE_ACCOUNT_PREFERENCES': {
      const { payload } = action;

      const {
        parental_controls: parentalControls,
        preferences: { pref_hide_sd_dups: hideSdDups, current_receiver: currentReceiver },
      } = payload;

      return {
        ...state,
        accountPreferences: {
          fetching: false,
          error: false,
          preferences: {
            parentalControls,
            sdHdDups: {
              hideSdDups,
              fetching: false,
              error: false,
            },
            currentReceiver,
          },
        },
      };
    }
    case 'REQUEST_ACCOUNT_PREFERENCES_ERROR': {
      return {
        ...state,
        accountPreferences: {
          fetching: false,
          error: true,
        },
      };
    }
    case 'CLEAR_REMOVE_ERROR_MESSAGE': {
      return {
        ...state,
        removeDevice: {
          ...state.removeDevice,
          errorDescription: '',
        },
      };
    }

    case 'REQUEST_UPDATE_CAROUSEL_PREFERENCES': {
      const {
        payload: { carousels },
      } = action;
      const profilePreferences = state.profilePreferences.preferences;

      if (!profilePreferences) {
        return state;
      }

      return {
        ...state,
        profilePreferences: {
          ...state.profilePreferences,
          preferences: {
            ...profilePreferences,
            carousels,
          },
        },
      };
    }
    case 'RECEIVE_UPDATE_CAROUSEL_PREFERENCES':
      return state;
    case 'REQUEST_UPDATE_CAROUSEL_PREFERENCES_ERROR':
      return state;

    case 'REQUEST_RESET_CAROUSEL_PREFERENCES':
      return state;
    case 'RECEIVE_RESET_CAROUSEL_PREFERENCES': {
      const { payload: carousels } = action;
      const profilePreferences = state.profilePreferences.preferences;

      if (!profilePreferences) {
        return state;
      }

      return {
        ...state,
        profilePreferences: {
          ...state.profilePreferences,
          preferences: {
            ...profilePreferences,
            carousels,
          },
        },
      };
    }
    case 'REQUEST_RESET_CAROUSEL_PREFERENCES_ERROR':
      return state;

    case 'REQUEST_CHANGE_SD_HD_DUPS_PREFERENCE': {
      const { accountPreferences } = state;
      const { preferences } = accountPreferences;

      if (!preferences) {
        return state;
      }

      const {
        payload: { hideSdDups },
      } = action;

      return {
        ...state,
        accountPreferences: {
          ...accountPreferences,
          preferences: {
            ...preferences,
            sdHdDups: {
              hideSdDups,
              fetching: true,
              error: false,
            },
          },
        },
      };
    }
    case 'RECEIVE_CHANGE_SD_HD_DUPS_PREFERENCE':
      return {
        ...state,
        accountPreferences: {
          ...state.accountPreferences,
          preferences: {
            ...state.accountPreferences.preferences,
            sdHdDups: {
              ...state.accountPreferences.preferences?.sdHdDups,
              fetching: false,
              error: false,
            },
          },
        },
      };
    case 'REQUEST_CHANGE_SD_HD_DUPS_PREFERENCE_ERROR':
      return {
        ...state,
        accountPreferences: {
          ...state.accountPreferences,
          preferences: {
            ...state.accountPreferences.preferences,
            sdHdDups: {
              ...state.accountPreferences.preferences?.sdHdDups,
              fetching: false,
              error: true,
            },
          },
        },
      };

    case 'REQUEST_SHOW_SCORES_ON_SPORT_PAGE_PREFERENCE':
      return {
        ...state,
        gameFinderPreferences: {
          fetching: false,
          errorFetching: false,
          errorSaving: false,
        },
      };
    case 'RECEIVE_SHOW_SCORES_ON_SPORT_PAGE_PREFERENCE': {
      const {
        payload: { pref_gamefinder_show_scores: gamefinderShowScores },
      } = action;

      return {
        ...state,
        gameFinderPreferences: {
          showScores:
            gamefinderShowScores !== null
              ? Boolean(gamefinderShowScores)
              : state.gameFinderPreferences.showScores,
          fetching: false,
          errorFetching: false,
          errorSaving: false,
        },
      };
    }
    case 'REQUEST_SHOW_SCORES_ON_SPORT_PAGE_PREFERENCE_ERROR':
      return {
        ...state,
        gameFinderPreferences: {
          ...state.gameFinderPreferences,
          fetching: false,
          errorFetching: true,
        },
      };
    case 'REQUEST_SET_SHOW_SCORES_ON_SPORT_PAGE': {
      return {
        ...state,
        gameFinderPreferences: {
          ...state.gameFinderPreferences,
          fetching: false,
          errorFetching: false,
          errorSaving: false,
        },
      };
    }
    case 'RECEIVE_SET_SHOW_SCORES_ON_SPORT_PAGE':
      return {
        ...state,
        gameFinderPreferences: {
          ...state.gameFinderPreferences,
          fetching: false,
          errorFetching: false,
          errorSaving: false,
        },
      };
    case 'REQUEST_SET_SHOW_SCORES_ON_SPORT_PAGE_ERROR':
      return {
        ...state,
        gameFinderPreferences: {
          ...state.gameFinderPreferences,
          fetching: false,
          errorFetching: false,
          errorSaving: true,
        },
      };
    case 'REQUEST_CHANGE_GUIDE_SORTING': {
      const {
        payload: { ascending },
      } = action;
      return {
        ...state,
        profilePreferences: {
          ...state.profilePreferences,
          preferences: {
            ...state.profilePreferences.preferences,
            guideChannelsSorting: {
              ascending,
              fetching: true,
              error: false,
            },
          },
        },
      };
    }
    case 'RECEIVE_CHANGE_GUIDE_SORTING':
      return {
        ...state,
        profilePreferences: {
          ...state.profilePreferences,
          preferences: {
            ...state.profilePreferences.preferences,
            guideChannelsSorting: {
              ...state.profilePreferences.preferences?.guideChannelsSorting,
              fetching: false,
              error: false,
            },
          },
        },
      };
    case 'REQUEST_CHANGE_GUIDE_SORTING_ERROR':
      return {
        ...state,
        profilePreferences: {
          ...state.profilePreferences,
          preferences: {
            ...state.profilePreferences.preferences,
            guideChannelsSorting: {
              ...state.profilePreferences.preferences?.guideChannelsSorting,
              fetching: false,
              error: true,
            },
          },
        },
      };
    case 'REQUEST_TIMER_PREFERENCES': {
      return {
        ...state,
        timerPreferences: {
          ...state.timerPreferences,
          fetching: true,
          errorFetching: false,
          errorSaving: false,
        },
      };
    }
    case 'RECEIVE_TIMER_PREFERENCES': {
      const {
        payload: {
          timer_properties: {
            /* Initially, radish may return empty timer preferences */
            endLate = DEFAULT_RECORD_SETTINGS.endLate.toString(),
            maxRecordings = DEFAULT_RECORD_SETTINGS.maxRecordings.toString(),
            priority = DEFAULT_RECORD_SETTINGS.priority.toString(),
            startEarly = DEFAULT_RECORD_SETTINGS.startEarly.toString(),
            keep = DEFAULT_RECORD_SETTINGS.keep.toString(),
          },
        },
      } = action;

      return {
        ...state,
        timerPreferences: {
          fetching: false,
          errorFetching: false,
          errorSaving: false,
          preferences: {
            endLate,
            maxRecordings,
            priority: Math.min(1, +priority).toString(),
            startEarly,
            keep,
          },
        },
      };
    }
    case 'REQUEST_TIMER_PREFERENCES_ERROR': {
      return {
        ...state,
        timerPreferences: {
          ...state.timerPreferences,
          fetching: false,
          errorFetching: true,
          errorSaving: false,
        },
      };
    }
    case 'REQUEST_SAVE_TIMER_PREFERENCES': {
      const {
        timerPreferences: { preferences },
      } = state;

      if (!preferences) {
        return state;
      }

      const {
        payload: { timerPreferences },
      } = action;

      return {
        ...state,
        timerPreferences: {
          ...state.timerPreferences,
          errorSaving: false,
          preferences: timerPreferences,
        },
      };
    }
    case 'RECEIVE_SAVE_TIMER_PREFERENCES': {
      return {
        ...state,
        timerPreferences: {
          ...state.timerPreferences,
          errorSaving: false,
        },
      };
    }
    case 'REQUEST_SAVE_TIMER_PREFERENCES_ERROR': {
      const {
        timerPreferences: { preferences },
      } = state;

      if (!preferences) {
        return state;
      }

      const {
        payload: { subPreferenceType, valueBeforeUpdate },
      } = action;

      return {
        ...state,
        timerPreferences: {
          ...state.timerPreferences,
          errorSaving: true,
          preferences: updateTimerPreferences(preferences, subPreferenceType, valueBeforeUpdate)
            .updatedPreferences,
        },
      };
    }
    case 'REQUEST_DYNA_DEVICES':
      return {
        ...state,
        dynaDevices: {
          ...state.dynaDevices,
          fetching: true,
        },
      };
    case 'RECEIVE_DYNA_DEVICES':
      return {
        ...state,
        dynaDevices: {
          fetching: false,
          error: false,
          devices: action.payload,
        },
      };
    case 'REQUEST_DYNA_DEVICES_ERROR':
      return {
        ...state,
        dynaDevices: {
          ...state.dynaDevices,
          error: true,
          fetching: false,
        },
      };
    case 'REQUEST_REGISTER_DYNA_DEVICE':
      return {
        ...state,
        dynaDevices: {
          ...state.dynaDevices,
          fetching: true,
          error: false,
        },
      };
    case 'RECEIVE_REGISTER_DYNA_DEVICE':
      return {
        ...state,
        dynaDevices: {
          ...state.dynaDevices,
          fetching: false,
          error: false,
          devices: [],
        },
      };
    case 'STORE_LEGACY_DEVICE_LIST':
      return {
        ...state,
        legacyDevices: {
          ...state.legacyDevices,
          devices: action.payload,
        },
      };
    case 'REQUEST_REGISTER_DYNA_DEVICE_ERROR':
      return {
        ...state,
        dynaDevices: {
          ...state.dynaDevices,
          fetching: false,
          error: true,
          devices: [],
        },
      };
    case 'REQUEST_DELETE_DYNA_DEVICE': {
      const { deviceId } = action.payload;
      return {
        ...state,
        dynaDevices: {
          ...state.dynaDevices,
          fetching: true,
          error: false,
        },
        removeDevice: {
          ...state.removeDevice,
          currentlyRemovingDevices: [...state.removeDevice.currentlyRemovingDevices, deviceId],
        },
      };
    }
    case 'RECEIVE_DELETE_DYNA_DEVICE': {
      const { deviceId } = action.payload;
      return {
        ...state,
        dynaDevices: {
          ...state.dynaDevices,
          devices: state.dynaDevices.devices.filter(device => device.device_id !== deviceId),
          fetching: false,
          error: false,
        },
        removeDevice: {
          errorDescription: '',
          currentlyRemovingDevices: state.removeDevice.currentlyRemovingDevices.filter(
            item => item !== deviceId,
          ),
        },
      };
    }
    case 'REQUEST_DELETE_DYNA_DEVICE_ERROR': {
      const { errorDescription, deviceId } = action.payload;
      return {
        ...state,
        dynaDevices: {
          ...state.dynaDevices,
          fetching: false,
          error: true,
        },
        removeDevice: {
          errorDescription,
          currentlyRemovingDevices: state.removeDevice.currentlyRemovingDevices.filter(
            item => item !== deviceId,
          ),
        },
      };
    }
    case 'REQUEST_DELETE_LEGACY_DEVICE': {
      return {
        ...state,
        legacyDevices: {
          ...state.legacyDevices,
          fetching: true,
          error: false,
        },
      };
    }
    case 'RECEIVE_DELETE_LEGACY_DEVICE': {
      return {
        ...state,
        legacyDevices: {
          ...state.legacyDevices,
          fetching: false,
          error: false,
        },
      };
    }
    case 'REQUEST_DELETE_LEGACY_DEVICE_ERROR': {
      return {
        ...state,
        legacyDevices: {
          ...state.legacyDevices,
          fetching: false,
          error: true,
        },
      };
    }
    case 'REQUEST_DYNA_DEVICE_CHECK':
      return {
        ...state,
      };
    case 'DYNA_DEVICE_LIMIT_REACHED':
      return {
        ...state,
      };
    // $FlowExpectedError
    case 'SIGN_OUT':
      return initialState;
    default: {
      /* Following line assures switch exhaustiveness */
      (action.type: empty); /* eslint-disable-line */
      return state;
    }
  }
};

export default reducer;
